import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as puntuacionService from "../../services/puntuacionService";
import { Grid, Card, CardContent, Snackbar, Alert } from '@mui/material';
import Puntuacion from '../../components/Puntuacion'
import PuntuacionDetalle from '../../components/PuntuacionDetalle'
import PuntuacionGrupo from '../../components/PuntuacionGrupo'
import MantenimientoForm from '../../components/common/MantenimientoForm';
import { useMantenimiento } from '../../hooks/useMantenimiento';
import { activeValidations as activeValidationsPuntuacion } from '../../redux/puntuacionSlice';
import { setEntity as setPuntuacionDetalle } from '../../redux/puntuacionDetalleSlice';
import { setEntity as setPuntuacionGrupo } from '../../redux/puntuacionGrupoSlice';

// Mantenimiento de la puntuacion: la escala y sus dos detalles. Los grupos
// se guardan con su subdetalle, asi que el guardado va por pasos.
const Edit = () => {
    const puntuacion = useSelector((state) => state.puntuacion.entity);
    const puntuacionDetalle = useSelector((state) => state.puntuacionDetalle.entity);
    const puntuacionGrupo = useSelector((state) => state.puntuacionGrupo.entity);
    const puntuacionValidation = useSelector((state) => state.puntuacion.validationMessage);
    const defaultPuntuacionDetalle = useSelector((state) => state.puntuacionDetalle.defaultEntity);
    const defaultPuntuacionGrupo = useSelector((state) => state.puntuacionGrupo.defaultEntity);

    const [openMessage, setOpenMessage] = useState(false)
    const onCloseMessage = () => { setOpenMessage(!openMessage); }
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });
    const dispatch = useDispatch();
    const params = useParams();
    const { isReadOnly, setIsReadOnly } = useMantenimiento("puntuacion");

    const mensaje = (severity, texto) => {
        setPropsMessage({ severity: severity, message: texto })
        setOpenMessage(true);
    }

    // Guarda la escala y despues sus detalles, que necesitan el id.
    const postEntity = async () => {
        if (puntuacionValidation.length > 0) {
            dispatch(activeValidationsPuntuacion());
            mensaje("error", puntuacionValidation.join(" - "));
            return;
        }

        try {
            const response = await puntuacionService.addUpdatePuntuacion({
                idPuntuacion: puntuacion.id,
                codigo: puntuacion.codigo,
                nombre: puntuacion.nombre,
                idTipo: puntuacion.idTipo,
                // Clase (catalogo 321/322): define en que listado del plan
                // entra la escala.
                idClase: puntuacion.idClase,
                valorMinimo: puntuacion.valorMinimo,
                valorMaximo: puntuacion.valorMaximo,
                idEstado: puntuacion.idEstado,
            });

            const idPuntuacion = response.data.data;

            // Valores de la escala.
            for (const detalle of puntuacionDetalle) {
                if (detalle.id == 0 && (detalle.texto == null || detalle.texto.trim() === "")) continue;

                await puntuacionService.addUpdatePuntuacionDetalle({
                    id: detalle.id,
                    idPuntuacion: idPuntuacion,
                    valor: detalle.valor,
                    texto: detalle.texto,
                    descripcion: detalle.descripcion,
                });
            }

            // Grupos, cada uno con su subdetalle.
            for (const grupo of puntuacionGrupo) {
                if (grupo.id == 0 && (grupo.descripcion == null || grupo.descripcion.trim() === "")) continue;

                await puntuacionService.addUpdatePuntuacionGrupoWithDetails({
                    id: grupo.id,
                    idPuntuacion: idPuntuacion,
                    descripcion: grupo.descripcion,
                    puntuacionGrupoDetalle: (grupo.puntuacionGrupoDetalle || [])
                        .map((sub) => ({ idPuntuacionDetalle: sub.idPuntuacionDetalle })),
                });
            }

            mensaje("success", "Se guardaron los datos con exito");
            setIsReadOnly(true);

            // Se recarga todo con los ids ya generados.
            const dataDetalles = await puntuacionService.getsPuntuacionDetalle(idPuntuacion).catch(() => []);
            dispatch(setPuntuacionDetalle((dataDetalles || []).map((row) => ({ ...row, numero: row.id }))));

            const grupos = await puntuacionService.getsPuntuacionGrupoPorPuntuacion(idPuntuacion).catch(() => []);
            const conDetalle = [];
            for (const grupo of (grupos || [])) {
                const subdetalle = await puntuacionService.getsPuntuacionGrupoDetalle(grupo.id).catch(() => []);
                conDetalle.push({
                    ...grupo,
                    numero: grupo.id,
                    puntuacionGrupoDetalle: (subdetalle || []).map((x) => ({ ...x })),
                });
            }
            dispatch(setPuntuacionGrupo(conDetalle));
        }
        catch (err) {
            console.log("Err: ", err);
            // Si el backend rechazo los datos (por ejemplo, sin clase), el
            // motivo viene en detail y es mas util que el generico.
            mensaje("error", err.response?.data?.detail || "Hubo un error al guardar");
        }
    }

    return (
        <div>
            <MantenimientoForm
                title={(parseInt(params.id) === 0 ? "Nuevo" : "Editar") + " Puntuacion"}
                backTo={"/puntuacion"}
                onSave={postEntity}
                disabled={isReadOnly}
            >
                <Grid container spacing={1} sx={{
                    "& .MuiGrid-root>.MuiGrid-item": {
                        paddingTop: "8px"
                    },
                }}>
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <Puntuacion isReadOnly={isReadOnly} />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <PuntuacionDetalle isReadOnly={isReadOnly} />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <PuntuacionGrupo isReadOnly={isReadOnly} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </MantenimientoForm>
            <Snackbar open={openMessage} autoHideDuration={5000} onClose={onCloseMessage}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={onCloseMessage} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

export default Edit